import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../../lib/adminApi";
import { TInput, TArea, Toggle, Btn, Card, Badge, MediaPicker } from "../ui";

const NEW_CAT = { name: "", slug: "", description: "", artwork: "", order: 99, active: true };

export default function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const refresh = () => adm.list("categories").then((c) => setItems(c.sort((a, b) => (a.order || 0) - (b.order || 0)))).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const set = (k, v) => setEditing((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      const { _id, id, source, createdAt, updatedAt, ...body } = editing;
      if (id) await adm.update("categories", id, body);
      else await adm.create("categories", body);
      toast.success("Category saved.");
      setEditing(null); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const destroy = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Products keep working — they simply lose this tag.`)) return;
    try { await adm.remove("categories", c.id); refresh(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="store-categories-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="font-mono text-[9px] tracking-[0.25em] text-white/40 max-w-xl">
          CATEGORIES ORGANIZE THE PUBLIC STORE. ADD AS MANY AS YOU NEED — NO CODE CHANGES REQUIRED.
        </p>
        <Btn onClick={() => setEditing(NEW_CAT)} testid="category-new">NEW CATEGORY</Btn>
      </div>

      {editing && (
        <Card className="mb-6 grid md:grid-cols-4 gap-4">
          <TInput label="NAME" value={editing.name} onChange={(v) => set("name", v)} testid="category-name" />
          <TInput label="SLUG (URL — AUTO IF BLANK)" value={editing.slug} onChange={(v) => set("slug", v)} testid="category-slug" />
          <TInput label="DISPLAY ORDER" value={editing.order} onChange={(v) => set("order", Number(v) || 0)} type="number" testid="category-order" />
          <div className="flex items-end pb-1">
            <Toggle label="ACTIVE (VISIBLE IN STORE)" checked={editing.active !== false} onChange={(v) => set("active", v)} testid="category-active" />
          </div>
          <TArea label="DESCRIPTION (OPTIONAL)" value={editing.description} onChange={(v) => set("description", v)} className="md:col-span-4" rows={2} />
          <div className="md:col-span-4">
            <MediaPicker label="CATEGORY ARTWORK (OPTIONAL — SHOWN AS A BANNER WHEN THE CATEGORY IS SELECTED)"
              value={editing.artwork} onChange={(v) => set("artwork", v || "")} mode="url" filterType="image" testid="category-artwork" />
          </div>
          <div className="flex gap-3 md:col-span-4">
            <Btn onClick={save} testid="category-save">SAVE</Btn>
            <Btn variant="ghost" onClick={() => setEditing(null)}>CANCEL</Btn>
          </div>
        </Card>
      )}

      {items.map((c) => (
        <div key={c.id} className="flex items-center justify-between gap-4 border-t border-white/10 py-4 px-2" data-testid={`category-row-${c.slug}`}>
          <div className="flex items-center gap-4 min-w-0">
            <span className="font-mono text-[10px] text-gold w-8">{String(c.order ?? 0).padStart(2, "0")}</span>
            <div className="min-w-0">
              <p className="text-white/90">{c.name}</p>
              <p className="font-mono text-[9px] text-white/40 mt-0.5">/{c.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {c.active !== false ? <Badge tone="green">ACTIVE</Badge> : <Badge tone="dim">HIDDEN</Badge>}
            <Btn variant="ghost" onClick={() => setEditing(c)} testid={`category-edit-${c.slug}`}>EDIT</Btn>
            <button onClick={() => destroy(c)} className="text-red-400/70 hover:text-red-400 text-xs px-1" data-testid={`category-delete-${c.slug}`}>✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}
